import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Fab,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  BugReport as BugIcon,
  Code as CodeIcon,
  Merge as MergeIcon,
  List as ListIcon,
} from "@mui/icons-material";
import { mergeRecipes } from "../../Validation/MergeTasks";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`debug-tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

const CodeDialog = ({ open, onClose, title, code }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {title}
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Box
        component="pre"
        sx={{
          p: 2,
          borderRadius: 1,
          bgcolor: "grey.100",
          overflow: "auto",
          maxHeight: "70vh",
          "& code": {
            fontFamily: "monospace",
            fontSize: "0.875rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          },
        }}
      >
        <code>{code}</code>
      </Box>
    </DialogContent>
  </Dialog>
);

const DebugPanel = ({
  show,
  onToggle,
  currentTasks,
  previousTasks,
  stationSelected,
  generateCode,
  generateMergedCode,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [codeDialog, setCodeDialog] = useState({
    open: false,
    title: "",
    code: "",
  });

  let newTasks =
    previousTasks?.tasks?.length > 0
      ? [...previousTasks?.tasks, ...currentTasks]
      : currentTasks;

  const mergedTasks =
    previousTasks?.tasks?.length > 0 ? mergeRecipes(newTasks) : newTasks;
  const mergeTaskBeforeGeneratingCode = mergedTasks;
  // console.log(
  //   "mergedTask from generateMergedCode : mergeTaskBeforeGeneratingCode",
  //   mergeTaskBeforeGeneratingCode
  // );

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const renderJson = (data) => (
    <pre
      style={{
        fontSize: "0.8rem",
        background: "rgba(0,0,0,0.04)",
        padding: "8px",
        borderRadius: "4px",
        maxHeight: "300px",
        overflow: "auto",
      }}
    >
      {JSON.stringify(data, null, 2)}
    </pre>
  );

  const renderCodeBlock = (code) => (
    <pre
      style={{
        fontSize: "0.8rem",
        background: "rgba(0,0,0,0.04)",
        padding: "8px",
        borderRadius: "4px",
        maxHeight: "300px",
        overflow: "auto",
        whiteSpace: "pre-wrap",
      }}
    >
      {code}
    </pre>
  );

  const showCode = (title, code) => {
    setCodeDialog({
      open: true,
      title,
      code,
    });
  };

  return (
    <Box sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1000 }}>
      <Fab
        color={show ? "secondary" : "primary"}
        onClick={onToggle}
        size="medium"
      >
        {show ? <CloseIcon /> : <BugIcon />}
      </Fab>

      <Collapse in={show && expanded} sx={{ mt: 2 }}>
        <Paper
          sx={{
            width: 400,
            maxHeight: "80vh",
            overflow: "auto",
            p: 2,
            boxShadow: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Debug Panel
          </Typography>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab icon={<ListIcon />} label="Tasks" />
            <Tab icon={<CodeIcon />} label="Code" />
            {previousTasks && <Tab icon={<MergeIcon />} label="Merged" />}
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <Typography variant="subtitle2" gutterBottom>
              Current Recipe Tasks:
            </Typography>
            {renderJson(currentTasks)}

            {previousTasks && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Previous Recipe Tasks:
                </Typography>
                {renderJson(previousTasks)}
              </>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={() => {
                const code = generateCode(currentTasks, stationSelected);
                showCode("Current Recipe Code", code);
              }}
              fullWidth
              sx={{ mb: 2 }}
            >
              Show Current Recipe Code
            </Button>

            {previousTasks && (
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => {
                  const code = generateCode(
                    previousTasks.tasks,
                    previousTasks.station
                  );
                  showCode("Previous Recipe Code", code);
                }}
                fullWidth
              >
                Show Previous Recipe Code
              </Button>
            )}
          </TabPanel>

          {previousTasks && (
            <TabPanel value={activeTab} index={2}>
              <Typography variant="subtitle2" gutterBottom>
                Merged Tasks Preview:
              </Typography>
              {renderJson(mergeTaskBeforeGeneratingCode)}

              <Button
                variant="contained"
                startIcon={<MergeIcon />}
                onClick={() => {
                  const mergedCode = generateMergedCode(
                    mergeTaskBeforeGeneratingCode
                  );
                  showCode("Merged Recipe Code", mergedCode);
                }}
                fullWidth
                sx={{ mt: 2 }}
              >
                Show Merged Code
              </Button>
            </TabPanel>
          )}
        </Paper>
      </Collapse>

      <CodeDialog
        open={codeDialog.open}
        onClose={() => setCodeDialog({ ...codeDialog, open: false })}
        title={codeDialog.title}
        code={codeDialog.code}
      />
    </Box>
  );
};

export default React.memo(DebugPanel);
